import React, { useState } from 'react';
import { AlertTriangle, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '../Button';
import { MIN_REASON_LENGTH, pruefeBegruendung } from '../../services/projectDeletionLogic';

interface Props {
    projektName: string;
    projektNummer?: string | null;
    /** Wirft bei Ablehnung durch die Datenbank — der Text landet im Dialog. */
    onBeantragen: (begruendung: string) => Promise<void>;
    onAbbrechen: () => void;
}

/**
 * Die Begruendungspflicht — Schritt 1 des Vier-Augen-Prinzips
 * (supabase/project_deletion_approvals.sql, MIGRATIONS.md Nr. 49).
 *
 * Der Dialog loescht NICHTS. Er stellt einen Antrag; wer ihn freigibt, lost
 * die Datenbank aus. Das steht ausdruecklich auf dem Schirm, weil die alte
 * Maske an derselben Stelle sofort geloescht hat und der Muskelgedaechtnis-
 * Klick sonst ins Leere trifft.
 *
 * Die Laengenpruefung ist eine Vorhersage des CHECKs `pdr_reason_chk`, keine
 * eigene Regel: der Server weist zu kurze Begruendungen ohnehin ab. Hier steht
 * sie nur, damit der Mangel VOR dem Absenden sichtbar ist.
 */
export const ProjectDeleteRequestDialog: React.FC<Props> = ({
    projektName,
    projektNummer,
    onBeantragen,
    onAbbrechen,
}) => {
    const [begruendung, setBegruendung] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const mangel = pruefeBegruendung(begruendung);
    const uebrig = Math.max(0, MIN_REASON_LENGTH - begruendung.trim().length);

    const absenden = async () => {
        if (mangel) return;
        setLaeuft(true);
        setFehler(null);
        try {
            await onBeantragen(begruendung.trim());
        } catch (e: any) {
            setFehler(e?.message || 'Der Antrag konnte nicht gestellt werden.');
            setLaeuft(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl animate-scale-in">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-red-50 p-3 rounded-full"><Trash2 className="w-6 h-6 text-red-600" /></div>
                    <div>
                        <h3 className="font-bold text-lg text-slate-800">Löschung beantragen</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            {projektNummer ? `${projektNummer} — ` : ''}{projektName}
                        </p>
                    </div>
                </div>

                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 mb-4 flex gap-2">
                    <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 leading-relaxed">
                        <strong>Vier-Augen-Prinzip.</strong> Dieser Schritt löscht noch nichts. Ein
                        zweiter Administrator wird zufällig ausgelost und muss zustimmen; erst danach
                        lässt sich das Projekt endgültig entfernen. Vor dem Löschen legt das System
                        eine vollständige Sicherung des Projekts an.
                    </p>
                </div>

                <label className="block text-sm font-bold text-slate-700 mb-1" htmlFor="loesch-begruendung">
                    Warum soll das Projekt weg?
                </label>
                <textarea
                    id="loesch-begruendung"
                    autoFocus
                    rows={4}
                    value={begruendung}
                    onChange={(e) => setBegruendung(e.target.value)}
                    disabled={laeuft}
                    placeholder="z. B.: Versehentlich doppelt angelegt, alle Berichte hängen an 6212."
                    className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none disabled:bg-slate-50"
                />
                <p className={`text-xs mt-1 ${mangel ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {mangel ? (uebrig > 0 ? `Noch ${uebrig} Zeichen.` : mangel) : 'Begründung ausreichend.'}
                    {' '}Sie steht dauerhaft im Löschprotokoll.
                </p>

                {fehler && (
                    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-rose-800 leading-relaxed">{fehler}</p>
                    </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <Button variant="ghost" onClick={onAbbrechen} disabled={laeuft}>Abbrechen</Button>
                    <Button
                        variant="danger"
                        onClick={absenden}
                        disabled={!!mangel || laeuft}
                        icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                    >
                        {laeuft ? 'Wird gestellt…' : 'Antrag stellen'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
